import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// ─── Config ──────────────────────────────────────────────────────────

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
});

// ─── Sign Up ─────────────────────────────────────────────────────────

export function signUp(
  email: string,
  password: string,
  displayName: string,
  inviteCode?: string,
): Promise<CognitoUser> {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: displayName }),
    ];

    const clientMetadata = inviteCode ? { inviteCode } : undefined;

    userPool.signUp(email, password, attributes, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result!.user);
    }, clientMetadata);
  });
}

// ─── Verify (Confirm Sign Up) ────────────────────────────────────────

export function verifyEmail(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

// ─── Resend Verification Code ────────────────────────────────────────

export function resendVerificationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

// ─── Sign In ─────────────────────────────────────────────────────────

export function signIn(
  email: string,
  password: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

// ─── Get Current Session ─────────────────────────────────────────────

export function getSession(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        resolve(session);
      },
    );
  });
}

// ─── Get ID Token ────────────────────────────────────────────────────

export async function getIdToken(): Promise<string | null> {
  const session = await getSession();
  if (!session) return null;
  return session.getIdToken().getJwtToken();
}

// ─── Get Current User Info ───────────────────────────────────────────

export function getCurrentUser(): Promise<{ userId: string; email: string; displayName: string } | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }

      cognitoUser.getUserAttributes((attrErr, attributes) => {
        if (attrErr || !attributes) {
          // Fall back to token claims
          const payload = session.getIdToken().payload;
          resolve({
            userId: payload['sub'] as string,
            email: payload['email'] as string,
            displayName: (payload['name'] as string) || '',
          });
          return;
        }

        const attrMap: Record<string, string> = {};
        for (const attr of attributes) {
          attrMap[attr.getName()] = attr.getValue();
        }

        resolve({
          userId: attrMap['sub'] || '',
          email: attrMap['email'] || '',
          displayName: attrMap['name'] || '',
        });
      });
    });
  });
}

// ─── Sign Out ────────────────────────────────────────────────────────

export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

// ─── Forgot Password ────────────────────────────────────────────────

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
}

// ─── Confirm New Password ───────────────────────────────────────────

export function confirmNewPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}
